import { Link } from "react-router-dom";
import MarketplaceShell, {
  C,
  SERIF,
  card,
  CardTitle,
  ClosingBand,
  IconTile,
} from "./MarketplaceShell";

/**
 * About, at /about — built from the Claude Design file `About.dc.html`.
 *
 * <p><b>Everything stated here is checkable against the product.</b> The coverage, what a listing
 * is, how enquiries travel, how members are billed — all of it describes behaviour that exists in
 * the code.</p>
 *
 * <p>The earlier version of this page carried a "needs your review" block asking for a founder
 * story, because a page called About with no company in it felt unfinished. The design answers that:
 * it has no founder section, no team and no customer count, and says what the business does instead.
 * That is a deliberate choice and a better one than inventing history — so the block is gone rather
 * than bolted onto a design that did not ask for it.</p>
 */

const MONEY_POINTS = [
  {
    title: "No pay-per-lead",
    body: "We don't charge per lead and we don't take a commission on work you win. We're not a party to whatever you agree with a customer.",
  },
  {
    title: "Your enquiry stays yours",
    body: "An enquiry goes to the business it was sent to — we don't sell the same enquiry to four people and let them race.",
  },
  {
    title: "Promoted means promoted",
    body: "Businesses can pay to be featured at the top of the marketplace for a week. Those listings are labelled as promoted wherever they appear — a paid position that reads as an endorsement would devalue every other listing.",
  },
];

const body: React.CSSProperties = { fontSize: 14, color: C.bodyMuted, lineHeight: 1.7 };

/** One of the two bordered boxes inside "Who it's for". */
function Audience({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        padding: "14px 16px",
        background: C.ground,
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 700, color: C.deep, marginBottom: 4 }}>{heading}</div>
      <div style={{ fontSize: 13.5, color: C.bodyMuted, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

export default function PartnerNetworkAbout() {
  return (
    <MarketplaceShell
      metaTitle="About"
      metaDescription="What DAconnect is, who the marketplace is for, and how it works."
      eyebrow="ABOUT"
      title={
        <>
          Development intelligence,{" "}
          {/* Mint, not deep green: the accent sits on a deep-green wash over the photograph, and
              deep-on-deep is the one pairing in this palette that disappears. */}
          <b style={{ color: C.mint, fontWeight: 600 }}>and the people who do the work</b>.
        </>
      }
      intro="We track development applications across South Australia, southern Queensland and New South Wales, and we run a marketplace where the trades and suppliers doing that work can be found."
      heroImage={{ src: "/images/partner-network/about-hero.jpg" }}
      showBack
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 24 }}>
        {/* WHAT WE DO */}
        <section data-testid="about-what" style={{ ...card, padding: "30px 32px" }}>
          <div style={{ marginBottom: 14 }}>
            <IconTile glyph="🗂" />
          </div>
          <div style={{ marginBottom: 10 }}>
            <CardTitle>What we do</CardTitle>
          </div>
          <p style={{ ...body, margin: 0 }}>
            Every week, councils publish development applications — new builds, extensions, fit-outs,
            demolitions. DAconnect collects them, keeps them current, and makes them searchable by
            council, suburb and category, with alerts when something matching your work appears.
          </p>
          <p style={{ ...body, marginTop: 12, marginBottom: 0 }}>
            The marketplace is the other side of that: owners and developers with a project can search
            for trades and suppliers who actually work in their council area, and contact them
            directly.
          </p>
        </section>

        {/* WHO IT'S FOR */}
        <section data-testid="about-who" style={{ ...card, padding: "30px 32px" }}>
          <div style={{ marginBottom: 14 }}>
            <IconTile glyph="⚒" />
          </div>
          <div style={{ marginBottom: 10 }}>
            <CardTitle>Who it's for</CardTitle>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Audience heading="If you're building something">
              Searching and contacting a business costs nothing, and there's no account to create. You
              deal with the business directly from their profile.
            </Audience>
            <Audience heading="If you're a trade or supplier">
              {/* The design pointed this at #pricing; the page exists, so it goes there. */}
              A listing puts you in front of people whose projects have just been lodged with council,
              at a flat monthly price. See{" "}
              <Link to="/pricing" style={{ fontWeight: 700 }}>pricing</Link>.
            </Audience>
          </div>
        </section>
      </div>

      {/* HOW WE MAKE MONEY */}
      <section
        data-testid="about-money"
        style={{
          background: C.deep,
          color: "#fff",
          borderRadius: 20,
          marginTop: 24,
          padding: 34,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
          gap: "24px 40px",
          boxShadow: "0 20px 50px rgba(20,83,45,.18)",
        }}
      >
        <div style={{ gridColumn: "1/-1" }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, margin: 0 }}>
            How we make money
          </h2>
          <div style={{ fontSize: 14, color: C.mintFaint, marginTop: 4 }}>
            Members pay a flat monthly subscription. That's it.
          </div>
        </div>
        {MONEY_POINTS.map((point) => (
          <div
            key={point.title}
            style={{ borderLeft: "2px solid rgba(134,239,172,.4)", paddingLeft: 16 }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 5, color: C.mint }}>
              {point.title}
            </div>
            <div style={{ fontSize: 13.5, color: C.mintInk, lineHeight: 1.65 }}>{point.body}</div>
          </div>
        ))}
      </section>

      {/* WHERE WE COVER */}
      <section
        data-testid="about-coverage"
        style={{
          ...card,
          marginTop: 24,
          padding: "30px 34px",
          display: "flex",
          gap: 28,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <IconTile glyph="🗺" />
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ marginBottom: 8 }}>
            <CardTitle>Where we cover</CardTitle>
          </div>
          <p style={{ ...body, margin: 0 }}>
            Development applications from councils across{" "}
            <b style={{ color: C.deep }}>South Australia</b>,{" "}
            <b style={{ color: C.deep }}>southern Queensland</b> and{" "}
            <b style={{ color: C.deep }}>New South Wales</b>, updated as councils publish them.
            Marketplace members are concentrated in those same areas — the directory shows you who
            genuinely serves your council rather than padding the list with businesses who don't.
          </p>
        </div>
      </section>

      <ClosingBand
        heading="Get in touch"
        detail="DAconnect Pty Ltd · ABN 74 168 911 402 · questions, problems or press."
        actionLabel="Contact us"
        actionTo="/contact"
      />
    </MarketplaceShell>
  );
}
